<template>
	<div class="mt-2">
		<template v-if="loading">
			<skeletonBuilder 
				:showChart="false"
				:cols="1"
				:rows="10"
			/>
		</template>

		<template v-else>
			<!-- HERO -->
			<b-card class="dashboard-hero-card mb-4" body-class="p-4 p-md-5">
				<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
					<div class="mb-3 mb-md-0">
						<h2 class="mb-2 text-white">{{ greetingText }}</h2>
						<p class="lead text-white-80 mb-0">Панель управления юридического отдела</p>
					</div>
                    <div class="text-nowrap hero-actions text-right">
                        <b-button
                            variant="light"
                            class="refresh-btn"
                            :disabled="isRefreshing"
                            @click="refreshData"
                            title="Обновить"
                            aria-label="Обновить"
                            style="width: 30px !important;"
                        >
                            <span v-if="isRefreshing"><i class="fas fa-spinner fa-spin"></i></span>
                            <span v-else aria-hidden="true">↻</span>
                        </b-button>
                    </div>
				</div>
			</b-card>

			<!-- KPIs -->
			<b-row>
				<b-col cols="12" md="6" xl="3" class="mb-4">
					<b-card class="stat-card h-100">
						<span class="help-icon" v-b-tooltip.hover title="Количество заявок на заполнении">
							<b-icon icon="question-circle"></b-icon>
						</span>
						<div class="d-flex align-items-end justify-content-between">
							<div>
								<div class="stat-label">На заполнении</div>
								<div class="stat-value text-brand-orange">
									<template v-if="dashboardLoading">
										<i class="fas fa-spinner fa-spin"></i>
									</template>
									<template v-else>
										{{ stats.fill }}
									</template>
								</div>
							</div>
						</div>
					</b-card>
				</b-col>

				<b-col cols="12" md="6" xl="3" class="mb-4">
					<b-card class="stat-card h-100">
						<span class="help-icon" v-b-tooltip.hover title="Количество заявок на рассмотрении и доработке">
							<b-icon icon="question-circle"></b-icon>
						</span>
						<div class="d-flex align-items-end justify-content-between">
							<div>
								<div class="stat-label">На рассмотрении</div>
								<div class="stat-value text-brand-blue">
									<template v-if="dashboardLoading">
										<i class="fas fa-spinner fa-spin"></i>
									</template>
									<template v-else>
										{{ stats.pending + stats.revision }}
									</template>
								</div>
							</div>
						</div>
					</b-card>
				</b-col>

				<b-col cols="12" md="6" xl="3" class="mb-4">
					<b-card class="stat-card h-100">
						<span class="help-icon" v-b-tooltip.hover title="Количество заявок с подписанным договором оферты">
							<b-icon icon="question-circle"></b-icon>
						</span>
						<div class="d-flex align-items-end justify-content-between">
							<div>
								<div class="stat-label">Подписано</div>
								<div class="stat-value text-brand-green">
									<template v-if="dashboardLoading">
										<i class="fas fa-spinner fa-spin"></i>
									</template>
									<template v-else>
										{{ stats.approved }}
									</template>
								</div>
							</div>
						</div>
					</b-card>
				</b-col>

				<b-col cols="12" md="6" xl="3" class="mb-4">
					<b-card class="stat-card h-100">
						<span class="help-icon" v-b-tooltip.hover title="Количество проверенных агентов">
							<b-icon icon="question-circle"></b-icon>
						</span>
						<div class="d-flex align-items-end justify-content-between">
							<div>
								<div class="stat-label">Проверено</div>
								<div class="stat-value text-brand-green">
									<template v-if="dashboardLoading">
										<i class="fas fa-spinner fa-spin"></i>
									</template>
									<template v-else>
										{{ stats.approved_by_company }}
									</template>
								</div>
							</div>
						</div>
					</b-card>
				</b-col>

			</b-row>

			<!-- Lists -->
			<b-row>
				<!-- Заявки на рассмотрение -->
				<b-col cols="12" lg="6" class="mb-4">
					<b-card class="stat-card h-100">
						<div class="d-flex align-items-center justify-content-between mb-3">
							<h5 class="mb-0">📋 Заявки на рассмотрение</h5>
							<b-button 
								size="sm" 
								variant="outline-primary" 
								@click="refreshData"
								:disabled="isRefreshing"
								title="Обновить"
							>
								<i class="fas fa-sync-alt"></i>
							</b-button>
						</div>
						<div v-if="dashboardLoading" class="text-center py-3">
							<i class="fas fa-spinner fa-spin"></i>
						</div>
						<div v-else-if="pendingApplications.length === 0" class="text-muted text-center py-4">
							<i class="fas fa-check-circle fa-2x mb-2"></i>
							<p>Нет заявок на рассмотрение</p>
						</div>
						<div v-else>
							<ul class="list-unstyled mb-0">
								<li v-for="application in pendingApplications" :key="application.id" class="media application-item py-2">
									<div class="media-body d-flex align-items-center justify-content-between w-100">
										<div>
											<div class="font-weight-600">{{ application.full_name || application.legal_name }}</div>
											<small class="text-muted">
												Статус: {{ application.check_status_label }}
											</small>
											<br>
											<small class="text-muted">
												Создана: {{ application.created_at }}
											</small>
										</div>
										<div class="text-right">
											<b-button 
												size="sm" 
												variant="outline-primary"
												@click="openApplication(application)"
												title="Открыть заявку"
											>
												Открыть
											</b-button>
										</div>
									</div>
								</li>
							</ul>
						</div>
					</b-card>
				</b-col>

				<!-- Заявки на расторжение -->
				<b-col cols="12" lg="6" class="mb-4">
					<b-card class="stat-card h-100">
						<div class="d-flex align-items-center justify-content-between mb-3">
							<h5 class="mb-0">🗑️ Заявки на расторжение</h5>
							<b-button 
								size="sm" 
								variant="outline-danger" 
								@click="refreshData"
								:disabled="isRefreshing"
								title="Обновить"
							>
								<i class="fas fa-sync-alt"></i>
							</b-button>
						</div>
						<div v-if="dashboardLoading" class="text-center py-3">
							<i class="fas fa-spinner fa-spin"></i>
						</div>
						<div v-else-if="terminationApplications.length === 0" class="text-muted text-center py-4">
							<i class="fas fa-trash-alt fa-2x mb-2"></i>
							<p>Нет заявок на расторжение</p>
						</div>
						<div v-else>
							<ul class="list-unstyled mb-0">
								<li v-for="application in terminationApplications" :key="application.id" class="media application-item py-2">
									<div class="media-body d-flex align-items-center justify-content-between w-100">
										<div>
											<div class="font-weight-600">{{ application.full_name || application.legal_name }}</div>
											<small class="text-muted">
												{{ application.check_status_label }}
											</small>
											<br>
											<small class="text-muted">
												Запрошено: {{ application.contract_terminated_at || application.updated_at }}
											</small>
										</div>
										<div class="text-right">
											<b-button 
												size="sm" 
												variant="outline-danger"
												@click="openApplication(application)"
												title="Открыть заявку"
											>
												Открыть
											</b-button>
										</div>
									</div>
								</li>
							</ul>
						</div>
					</b-card>
				</b-col>
			</b-row>

			<!-- Сайдбар для редактирования профиля -->
			<b-sidebar
				v-model="isSidebarOpen"
				id="sidebar-right-agent-company-profile"
				right
				backdrop
				shadow
				width="65em"
				backdrop-variant="dark"
				ref="agentCompanyProfile"
				no-close-on-backdrop
			>
				<template #title>
					<div>
						<strong>Профиль агентской компании</strong>
					</div>
				</template>
				<div class="d-block">
					<AgentCompanyProfile 
						v-if="selectedCompany"
						:agent_company="selectedCompany" 
						@closed="closeSidebar"
						@updated="onProfileUpdated"
					/>
				</div>
			</b-sidebar>
		</template>
	</div>
</template>

<script>
    import { mapGetters } from 'vuex';
    
    import SkeletonBuilder from "@/components/ui/Loaders/SkeletonBuilder";
    import AgentCompanyProfile from "@/components/agentCompanies/AgentCompanyProfile";
    import { API_AGENT_COMPANY } from "@/constants/api";

    export default {
        components: {
            SkeletonBuilder,
            AgentCompanyProfile
        },
        data() {
            return {
                loading: true,
                dashboardLoading: true,
                isRefreshing: false,
                pendingApplications: [],
                terminationApplications: [],
                stats: {
                    fill: 0,
                    pending: 0,
                    revision: 0,
                    approved: 0,
                    rejected: 0,
                    termination_by_agent: 0,
                    termination_by_company: 0
                },
                isSidebarOpen: false,
                selectedCompany: null
            }
        },
        computed: {
            ...mapGetters('auth', ['currentUser']),

            currentUser() {
                return this.$store.getters['auth/currentUser'];
            },

            greetingText() {
                const hours = new Date().getHours();
                const dayPart = hours < 12 ? 'Доброе утро' : (hours < 18 ? 'Добрый день' : 'Добрый вечер');
                
                let userName = 'юрист';
                if (this.currentUser) {
                    if (this.currentUser.first_name) {
                        userName = this.currentUser.first_name;
                        if (this.currentUser.middle_name) {
                            userName += ' ' + this.currentUser.middle_name;
                        }
                    } else if (this.currentUser.name) {
                        userName = this.currentUser.name;
                    }
                }
                
                return `${dayPart}, ${userName}!`;
            }
        },
        mounted() {
            this.loadData();
        },
        methods: {
            async loadData() {
                try {
                    this.loading = true;
                    this.dashboardLoading = true;
                    
                    // Загружаем данные агентских компаний
                    const { data } = await api.call('get', API_AGENT_COMPANY, {
                        params: { per_page: 1000 }
                    });
                    
                    if (data.data && Array.isArray(data.data)) {
                        this.processAgentCompanies(data.data);
                    }
                    
                } catch (error) {
                    console.error('Ошибка загрузки данных:', error);
                    this.$bvToast.toast('Ошибка загрузки данных', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                } finally {
                    this.loading = false;
                    this.dashboardLoading = false;
                }
            },
            
            processAgentCompanies(companies) {
                // Сбрасываем статистику
                this.stats = {
                    fill: 0,
                    pending: 0,
                    revision: 0,
                    approved: 0,
                    approved_by_company: 0,
                    rejected: 0,
                    termination_by_agent: 0,
                    termination_by_company: 0
                };
                
                // Фильтруем заявки на рассмотрение (pending, revision)
                this.pendingApplications = companies.filter(company => 
                    company.check_status === 'pending' || company.check_status === 'revision'
                );
                
                // Фильтруем заявки на расторжение
                this.terminationApplications = companies.filter(company => 
                    company.check_status === 'termination_by_agent' || company.check_status === 'termination_by_company'
                );
                
                // Подсчитываем статистику
                companies.forEach(company => {
                    if (this.stats.hasOwnProperty(company.check_status)) {
                        this.stats[company.check_status]++;
                    }
                });
            },
            
            async refreshData() {
                if (this.isRefreshing) return;
                
                this.isRefreshing = true;
                try {
                    await this.loadData();
                    this.$bvToast.toast('Данные обновлены', {
                        title: 'Успешно',
                        variant: 'success',
                        solid: true
                    });
                } catch (error) {
                    console.error('Ошибка обновления:', error);
                } finally {
                    setTimeout(() => {
                        this.isRefreshing = false;
                    }, 500);
                }
            },
            
            openApplication(application) {
                // Открываем сайдбар с профилем компании
                this.selectedCompany = application;
                this.isSidebarOpen = true;
            },
            
            closeSidebar() {
                this.isSidebarOpen = false;
                this.selectedCompany = null;
            },
            
            onProfileUpdated(updatedData) {
                // Обновляем данные после изменения профиля
                this.closeSidebar();
                this.refreshData();
                
                this.$bvToast.toast('Профиль компании обновлен', {
                    title: 'Успешно',
                    variant: 'success',
                    solid: true
                });
            }
        }
    }
</script>

<style scoped>
.dashboard-hero-card {
    background: linear-gradient(135deg, #0d6348 0%, #e37926 100%);
    border: none;
    color: #fff;
    border-radius: 14px;
}

.text-white-80 { color: rgba(255, 255, 255, 0.85); }

.text-brand-green { color: #0d6348; }
.text-brand-orange { color: #e37926; }
.text-brand-blue { color: #3498db; }
.text-brand-red { color: #e74c3c; }

.stat-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.05);
}

.stat-label {
    font-size: 0.85rem;
    line-height: 0.85rem;
    color: #6c757d;
    margin-bottom: 6px;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
}

.application-item {
    border-top: 1px solid #f2f2f2;
}
.application-item:first-child { border-top: 0; }

.font-weight-600 { font-weight: 600; }

.hero-actions .refresh-btn {
    font-size: 16px !important;
    width: 50px !important;
    height: 30px !important;
    line-height: 16px !important;
    padding: 0;
    opacity: 0.85;
    border-color: rgba(255,255,255,0.35) !important;
    color: rgba(255,255,255,0.9) !important;
	background: transparent !important;
}
.hero-actions .refresh-btn:hover {
    opacity: 1;
	border-color: #0d6348 !important;
}

.help-icon {
    position: absolute;
    right: 30px;
    cursor: pointer;
}
</style>